import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, X, FileText } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', body: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = () => {
    api.get('/api/templates').then(r => setTemplates(r.data.templates || [])).finally(() => setLoading(false));
  };

  const openCreate = () => {
    setForm({ name: '', subject: '', body: '' });
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (t) => {
    setForm({ name: t.name, subject: t.subject, body: t.body });
    setEditing(t.id);
    setCreating(false);
  };

  const save = async () => {
    if (!form.name || !form.subject || !form.body) return toast.error('All fields required');
    setSaving(true);
    try {
      if (editing) {
        const res = await api.put(`/api/templates/${editing}`, form);
        setTemplates(t => t.map(x => x.id === editing ? res.data.template : x));
        toast.success('Template updated');
      } else {
        const res = await api.post('/api/templates', form);
        setTemplates(t => [res.data.template, ...t]);
        toast.success('Template saved');
      }
      setEditing(null);
      setCreating(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/api/templates/${id}`);
    setTemplates(t => t.filter(x => x.id !== id));
    toast.success('Template deleted');
  };

  const vars = ['{{first_name}}', '{{last_name}}', '{{email}}', '{{company}}'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Email templates</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Save and reuse email content across campaigns</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14} /> New template</button>
      </div>

      {/* Create/Edit form */}
      {(creating || editing) && (
        <div className="card" style={{ padding: 22, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 18 }}>{editing ? 'Edit template' : 'New template'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Template name</label>
              <input className="input" placeholder="e.g. Cold outreach v1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Subject line</label>
              <input className="input" placeholder="Hey {{first_name}}, quick question" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--text2)' }}>Email body</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {vars.map(v => (
                    <button key={v} type="button" onClick={() => setForm(f => ({ ...f, body: f.body + v }))}
                      style={{ fontSize: 10, padding: '2px 7px', background: 'var(--blue-dim)', color: '#93c5fd', border: 'none', borderRadius: 20, cursor: 'pointer' }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <textarea className="input" rows={8} placeholder="Write your email body here…" style={{ resize: 'vertical', lineHeight: 1.6 }}
                value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                <Check size={14} /> {saving ? 'Saving…' : 'Save template'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setCreating(false); setEditing(null); }}>
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
      ) : templates.length === 0 && !creating ? (
        <div className="card" style={{ padding: 56, textAlign: 'center' }}>
          <FileText size={36} color="var(--text3)" style={{ marginBottom: 14 }} />
          <p style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>No templates yet</p>
          <p style={{ color: 'var(--text3)', fontSize: 12, marginBottom: 18 }}>Save your best emails as templates to reuse across campaigns</p>
          <button className="btn btn-primary" onClick={openCreate}><Plus size={14} /> Create first template</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {templates.map(t => (
            <div key={t.id} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--blue-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={16} color="#93c5fd" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Subject: {t.subject}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}>
                    {t.body.replace(/<[^>]+>/g, '').slice(0, 120)}…
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                    Updated {new Date(t.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><Edit2 size={13} /> Edit</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => deleteTemplate(t.id)}><Trash2 size={13} color="var(--red)" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
